import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getDepartments, createDepartment } from "../api";

const ADD_NEW = "__add_new__";

const defaultInputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db",
  fontSize: 13, boxSizing: "border-box",
};

/** Creatable department picker (spec: department-master-data, AC8). Replaces the
 * free-text Department input in Add Employee / profile editor — lets an admin pick
 * an existing department or type a new one inline without visiting the Departments
 * management screen first. */
export default function DepartmentSelect({
  value, onChange, style, disabled,
}: {
  value: string;
  onChange: (name: string) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ["departments"], queryFn: getDepartments });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createDepartment,
    onSuccess: (dept) => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      onChange(dept.name);
      setAdding(false);
      setDraft("");
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || "Could not create department");
    },
  });

  const active = (data?.departments || []).filter(d => d.status === "active");
  // Keep an already-assigned-but-inactive department visible so it isn't silently
  // cleared from the dropdown (AC7) — it just can't be picked for anyone new elsewhere.
  const options = value && !active.some(d => d.name === value)
    ? [...active, { id: -1, name: value, status: "inactive", employee_count: 0 }]
    : active;

  if (adding) {
    return (
      <div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            autoFocus
            value={draft}
            onChange={e => { setDraft(e.target.value); setError(null); }}
            placeholder="New department name"
            style={{ ...defaultInputStyle, ...style }}
            onKeyDown={e => {
              if (e.key === "Enter" && draft.trim()) { e.preventDefault(); createMut.mutate(draft.trim()); }
              if (e.key === "Escape") { setAdding(false); setDraft(""); setError(null); }
            }}
          />
          <button
            type="button"
            disabled={!draft.trim() || createMut.isPending}
            onClick={() => createMut.mutate(draft.trim())}
            style={{ padding: "8px 12px", borderRadius: 6, border: "none", background: "#1e3a5f", color: "white", fontSize: 13, cursor: "pointer" }}
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setDraft(""); setError(null); }}
            style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "white", fontSize: 13, cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
        {error && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>
    );
  }

  return (
    <select
      value={value || ""}
      disabled={disabled}
      onChange={e => {
        if (e.target.value === ADD_NEW) { setAdding(true); return; }
        onChange(e.target.value);
      }}
      style={{ ...defaultInputStyle, ...style }}
    >
      <option value="">— None —</option>
      {options.map(d => (
        <option key={d.id} value={d.name}>{d.name}{d.status !== "active" ? " (inactive)" : ""}</option>
      ))}
      <option value={ADD_NEW}>+ Add new department…</option>
    </select>
  );
}
